import React from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { CourseData } from '../../types/CourseData';

interface ConnectTabProps {
  selectedCourseData: CourseData | undefined;
  systemConfig: { Id: string; ConfigValue: string; }[];
  handleConnect: (courseId: number, source: 'click' | 'loop') => void;
  selectedCourse: number;
  handleUrlChange: (value: string) => void;
  setShowHowImage: React.Dispatch<React.SetStateAction<boolean>>;
  setImageSrc: React.Dispatch<React.SetStateAction<string>>;
  setModalSize: React.Dispatch<React.SetStateAction<string>>;
  KaiBlobLocation: string | undefined;
  safeAiIconSrc: string;
}

const ConnectTab: React.FC<ConnectTabProps> = ({
  selectedCourseData,
  systemConfig,
  handleConnect,
  selectedCourse,
  handleUrlChange,
  setShowHowImage,
  setImageSrc,
  setModalSize,
  KaiBlobLocation,
  safeAiIconSrc
}) => {
  const canvasUser = systemConfig.find(item => item.Id === 'canvas_system_user')?.ConfigValue || '';

  return (
    <div className="p-4 h-full">
      <div className="grid grid-cols-5 gap-6 p-4 border border-gray-300 rounded-md shadow-lg">
        {/* Left Column: Text Instructions */}
        <div className="col-span-3">
          <h3 className="text-sm font-semibold mb-6">
            Connect me with your Canvas course site.
            <br />
            You only have to do this once for this course.
          </h3>

          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                1
              </div>
              <div>
                <p>Open a separate browser tab or window and navigate to your Canvas course site.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                2
              </div>
              <div>
                <p>Add me to your Canvas course site as a <strong>SUPPORT</strong> or <strong>FACILITATOR</strong> user. My user ID is <strong>{canvasUser}</strong>.</p>
                <button
                  type="button"
                  className="text-gray-500 text-sm hover:underline mt-1 block p-0 bg-transparent border-none"
                  onClick={(e) => {
                    e.preventDefault();
                    setImageSrc(`${KaiBlobLocation}/static/canvas_how_do_i.jpg`);
                    setShowHowImage(true);
                    setModalSize('max-w-[60rem]');
                  }}
                >
                  How do I do that?
                </button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                3
              </div>
              <div className="flex-1">
                <p>Enter the URL of your Canvas course site.</p>
                <button
                  type="button"
                  className="text-gray-500 text-sm hover:underline mt-1 block p-0 bg-transparent border-none"
                  onClick={(e) => {
                    e.preventDefault();
                    setImageSrc(`${KaiBlobLocation}/static/canvas_where_do_i.jpg`);
                    setShowHowImage(true);
                    setModalSize('max-w-[40rem]');
                  }}
                >
                  Where do I find that?
                </button>
                <div className="flex items-center space-x-4 mt-2">
                  <input
                    type="text"
                    value={selectedCourseData?.url || ''}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="Eg: https://canvas.northwestern.edu/course/18760000000XXXXXX"
                    className="w-full flex-1 px-3 py-2 border rounded-md"
                    disabled={selectedCourseData?.connectionState === 'connecting' || selectedCourseData?.connectionState === 'connected'}
                  />
                </div>
                {selectedCourseData?.UrlError && (
                  <p className="text-red-500 text-sm mt-2">{selectedCourseData.UrlError}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Icons, Horizontal Dots, and Connect Button */}
        <div className="flex col-span-2 flex-col items-center justify-center space-y-4 h-full">
          {/* Two Icons with Horizontal Dots */}
          <div className="flex items-center space-x-4">
            {/* Left Icon */}
            {safeAiIconSrc ? (
              <img
                src={safeAiIconSrc}
                alt="AI Face"
                className="h-8 w-8 cursor-pointer"
              />
            ) : (
              <FontAwesomeIcon icon={faRobot} className="h-8 w-8 text-black" />
            )}

            {/* Horizontal Dots */}
            <div className="flex items-center space-x-2">
              {[...Array(5)].map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1 w-1 rounded-full ${
                    selectedCourseData?.connectionState === 'connecting'
                      ? 'bg-black animate-pulse'
                      : 'bg-black'
                  }`}
                  style={
                    selectedCourseData?.connectionState === 'connecting'
                      ? { animationDelay: `${idx * 150}ms` }
                      : {}
                  }
                />
              ))}
            </div>

            {/* Right Icon */}
            <img src="/canvas-logo.svg" alt="Canvas Logo" className="h-8 w-8" />
          </div>

          {/* Connect Button */}
          {selectedCourseData?.connectionState === 'idle' && (
            <button 
              onClick={() => selectedCourse && handleConnect(selectedCourse, 'click')}
              className="bg-black text-white hover:text-white hover:border-b-4 hover:bg-black py-2 px-6 border rounded-full whitespace-nowrap"
            >
              CONNECT
            </button>
          )}
                  
          {selectedCourseData?.connectionState === 'connecting' && (
            <button 
              className="pl-10 bg-blue-600 text-white py-2 px-8 rounded-md flex items-center space-x-4 cursor-not-allowed"
              disabled
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Connecting...</span>
            </button>
          )}
                
          {selectedCourseData?.connectionState === 'connected' && (
            <button 
              className="bg-green-600 text-white py-2 px-6 rounded-md flex items-center space-x-2 cursor-not-allowed"
              disabled
            >
              <CheckCircle2 className="h-4 w-4" />
              <span>Connected</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectTab;